/* ===== Announcements Page ===== */

import { useEffect, useState } from 'react'
import type { Announcement } from '@zetyar/domain'
import { Calendar, CheckCircle2, Pencil, Pin, Plus, Search, Trash2 } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import {
  createWebTenantContext,
  webAnnouncementRepository,
} from '@/infrastructure/repositories/announcements'
import { useReauthGuard } from '@/presentation/hooks/useReauthGuard'

export function AnnouncementsPage() {
  const { currentMembership, currentOrganization, profile, user, permissions } = useApp()
  const { guard, reauthModal } = useReauthGuard()
  const canCreate = profile?.isSuperAdmin || permissions.includes('announcements.create')
  const canEdit = profile?.isSuperAdmin || permissions.includes('announcements.update') || permissions.includes('announcements.create')
  const canDelete = profile?.isSuperAdmin || permissions.includes('announcements.delete')
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [isPinned, setIsPinned] = useState(false)

  const filteredAnnouncements = announcements.filter(
    (announcement) =>
      announcement.title.toLowerCase().includes(search.toLowerCase()) ||
      announcement.content.toLowerCase().includes(search.toLowerCase()),
  )

  useEffect(() => {
    let isMounted = true

    const loadAnnouncements = async () => {
      if (!currentOrganization || !profile || !user) {
        setAnnouncements([])
        return
      }

      try {
        setLoadError(null)
        const result = await webAnnouncementRepository.listVisible(
          createWebTenantContext({
            authUid: user.uid,
            personId: profile.id,
            organizationId: currentOrganization.id,
            membershipId: currentMembership?.id,
          }),
          { limit: 20 },
        )

        if (isMounted) {
          setAnnouncements(result.data)
        }
      } catch (error) {
        console.error('Error al cargar comunicados:', error)
        if (isMounted) {
          setAnnouncements([])
          setLoadError('No fue posible cargar los comunicados autorizados para esta organizacion.')
        }
      }
    }

    void loadAnnouncements()

    return () => {
      isMounted = false
    }
  }, [currentMembership, currentOrganization, profile, user])

  const handleCreateAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentOrganization || !profile || !user) return

    try {
      if (webAnnouncementRepository.create) {
        const created = await webAnnouncementRepository.create(
          createWebTenantContext({
            authUid: user.uid,
            personId: profile.id,
            organizationId: currentOrganization.id,
            membershipId: currentMembership?.id,
          }),
          {
            title,
            content,
            summary: content.substring(0, 100),
            isPinned,
            isDraft: false,
            audienceType: 'all',
          },
        )
        setAnnouncements([created, ...announcements])
      }
    } catch (error) {
      console.error('Error al crear el comunicado en Supabase:', error)
    }

    setShowCreateModal(false)
    setTitle('')
    setContent('')
    setIsPinned(false)
  }

  const handleOpenEditModal = (announcement: Announcement) => {
    setEditingAnnouncement(announcement)
    setTitle(announcement.title)
    setContent(announcement.content)
    setIsPinned(announcement.isPinned)
  }

  const handleUpdateAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentOrganization || !profile || !user || !editingAnnouncement) return

    try {
      if (webAnnouncementRepository.update) {
        const now = new Date().toISOString()
        await webAnnouncementRepository.update(
          createWebTenantContext({
            authUid: user.uid,
            personId: profile.id,
            organizationId: currentOrganization.id,
            membershipId: currentMembership?.id,
          }),
          editingAnnouncement.id,
          {
            title,
            content,
            summary: content.substring(0, 100),
            isPinned,
          },
        )

        setAnnouncements((current) =>
          current.map((item) =>
            item.id === editingAnnouncement.id
              ? {
                  ...item,
                  title,
                  content,
                  isPinned,
                  updatedAt: now,
                  isRead: false,
                }
              : item,
          ),
        )
      }
    } catch (error) {
      console.error('Error al editar el comunicado:', error)
    }

    setEditingAnnouncement(null)
    setTitle('')
    setContent('')
    setIsPinned(false)
  }

  const handleAcknowledgeRead = async (announcementId: string) => {
    if (!currentOrganization || !profile || !user) return

    await webAnnouncementRepository.acknowledgeRead(
      createWebTenantContext({
        authUid: user.uid,
        personId: profile.id,
        organizationId: currentOrganization.id,
        membershipId: currentMembership?.id,
      }),
      announcementId,
    )

    setAnnouncements((current) =>
      current.map((announcement) =>
        announcement.id === announcementId ? { ...announcement, isRead: true } : announcement,
      ),
    )
  }

  const handleDelete = (announcementId: string) => {
    guard(async () => {
      if (!currentOrganization || !profile || !user || !webAnnouncementRepository.delete) return

      try {
        await webAnnouncementRepository.delete(
          createWebTenantContext({
            authUid: user.uid,
            personId: profile.id,
            organizationId: currentOrganization.id,
            membershipId: currentMembership?.id,
          }),
          announcementId,
        )
        setAnnouncements((current) => current.filter((item) => item.id !== announcementId))
      } catch (error) {
        console.error('Error al eliminar el comunicado:', error)
      }
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Comunicados</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Comunicaciones oficiales para {currentOrganization?.name}
          </p>
        </div>
        {canCreate && (
          <Button
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => {
              setTitle('')
              setContent('')
              setIsPinned(false)
              setShowCreateModal(true)
            }}
          >
            Crear comunicado
          </Button>
        )}
      </div>

      <Card className="p-3">
        <Input
          placeholder="Buscar en comunicados..."
          leftIcon={<Search className="w-4 h-4 text-neutral-400" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Card>

      <div className="space-y-4">
        {loadError && (
          <Card className="border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20 text-sm text-amber-900 dark:text-amber-100">
            {loadError}
          </Card>
        )}

        {filteredAnnouncements.map((announcement) => {
          const isEdited = Boolean(
            announcement.updatedAt &&
              announcement.publishedAt &&
              new Date(announcement.updatedAt).getTime() - new Date(announcement.publishedAt).getTime() > 1000,
          )

          return (
            <Card key={announcement.id} className={announcement.isPinned ? 'border-primary-200 dark:border-primary-900 bg-primary-50/20 dark:bg-primary-950/20' : ''}>
              <div className="flex items-start justify-between">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {announcement.isPinned && (
                    <Badge variant="primary" className="flex items-center gap-1">
                      <Pin className="w-3 h-3" /> Destacado
                    </Badge>
                  )}
                  <span className="text-xs text-neutral-400 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(announcement.publishedAt!).toLocaleDateString('es-CL')}
                  </span>
                  {isEdited && (
                    <span className="text-xs text-neutral-500 dark:text-neutral-400 italic">
                      (Editado el {new Date(announcement.updatedAt!).toLocaleDateString('es-CL')} {new Date(announcement.updatedAt!).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })})
                    </span>
                  )}
                </div>
                <Badge variant="neutral">{announcement.readCount} confirmaciones</Badge>
              </div>

              <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mt-1">{announcement.title}</h2>
              <p className="text-sm text-neutral-700 dark:text-neutral-300 mt-2 whitespace-pre-line leading-relaxed">{announcement.content}</p>

              <div className="mt-4 pt-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
                <span>Publicado por <strong>{announcement.author?.displayName}</strong></span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={announcement.isRead ? 'secondary' : 'outline'}
                    leftIcon={<CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                    onClick={() => void handleAcknowledgeRead(announcement.id)}
                  >
                    {announcement.isRead ? 'Lectura confirmada' : 'Confirmar lectura'}
                  </Button>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0 flex items-center justify-center"
                      aria-label="Editar comunicado"
                      onClick={() => handleOpenEditModal(announcement)}
                    >
                      <Pencil className="w-3.5 h-3.5 text-neutral-600 dark:text-neutral-300" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0 flex items-center justify-center"
                      aria-label="Eliminar comunicado"
                      onClick={() => handleDelete(announcement.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-danger-600" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Publicar comunicado oficial"
      >
        <form onSubmit={handleCreateAnnouncement} className="space-y-4">
          <Input
            label="Titulo del comunicado"
            placeholder="Ej: Convocatoria a reunion o beneficio"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Contenido del comunicado</label>
            <textarea
              className="w-full h-32 p-3 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              placeholder="Escribe el mensaje completo..."
              required
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="pinned"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
              className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="pinned" className="text-sm text-neutral-700 dark:text-neutral-200 font-medium">
              Fijar como comunicado destacado
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Publicar comunicado
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(editingAnnouncement)}
        onClose={() => setEditingAnnouncement(null)}
        title="Editar comunicado oficial"
      >
        <form onSubmit={handleUpdateAnnouncement} className="space-y-4">
          <Input
            label="Titulo del comunicado"
            placeholder="Ej: Convocatoria a reunion o beneficio"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Contenido del comunicado</label>
            <textarea
              className="w-full h-32 p-3 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              placeholder="Escribe el mensaje completo..."
              required
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="edit-pinned"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
              className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="edit-pinned" className="text-sm text-neutral-700 dark:text-neutral-200 font-medium">
              Fijar como comunicado destacado
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setEditingAnnouncement(null)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Guardar cambios
            </Button>
          </div>
        </form>
      </Modal>

      {reauthModal}
    </div>
  )
}
